import React, { useEffect, useState, useRef } from 'react';
import { XIcon, UserIcon, TrendingUpIcon, TrendingDownIcon, MinusIcon, CalendarIcon, CheckSquareIcon, ExternalLinkIcon, CalendarPlusIcon, ListTodoIcon, BookOpenIcon, FileTextIcon, PencilIcon, XIcon as XIconSmall, CheckIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLessons } from '../../src/hooks/useLessons';
import { getTeacherTasks, Task } from '../../services/taskService';
import { getStudentPracticeLogs, PracticeLog } from '../../services/practiceLogService';
import { updateStudentNotes } from '../../services/studentService';
interface StudentProgressPanelProps {
  open: boolean;
  student: {
    id: string;
    studentId: string;
    name: string;
    instrument: string;
    grade?: number;
    progressTrend: string;
    trendDirection: 'up' | 'down' | 'neutral';
    practiceLevel: string;
    weeklyPracticeHours: string;
    attendanceSummary: string;
    attendanceValue: number;
    taskCompletionRate: string;
  } | null;
  onClose: () => void;
  onViewFullProfile: () => void;
  onMessage: () => void;
  darkMode: boolean;
}
export const StudentProgressPanel: React.FC<StudentProgressPanelProps> = ({
  open,
  student,
  onClose,
  onViewFullProfile,
  onMessage,
  darkMode
}) => {
  const navigate = useNavigate();
  const {
    currentUser
  } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);
  const [animation, setAnimation] = useState<'entering' | 'entered' | 'exiting' | 'exited'>('exited');
  const lastActiveElement = useRef<HTMLElement | null>(null);
  // State for last lesson
  const [lastLesson, setLastLesson] = useState<any>(null);
  const [loadingLesson, setLoadingLesson] = useState(false);
  // State for recent tasks
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  // Teacher notes state
  const [teacherNotes, setTeacherNotes] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSaveMessage, setNotesSaveMessage] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  // Fetch lessons for this student (extended range for better data)
  const today = new Date();
  const sixMonthsAgo = new Date(today);
  sixMonthsAgo.setMonth(today.getMonth() - 6);
  const threeMonthsAhead = new Date(today);
  threeMonthsAhead.setMonth(today.getMonth() + 3);
  const {
    lessons
  } = useLessons({
    teacherId: currentUser?.uid || '',
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    range: {
      fromISOUtc: sixMonthsAgo.toISOString(),
      toISOUtc: threeMonthsAhead.toISOString()
    }
  });
  // Load lesson data when student changes
  useEffect(() => {
    if (!student || !lessons.length) {
      setLastLesson(null);
      return;
    }
    setLoadingLesson(true);
    try {
      const now = new Date();
      // Filter lessons for this student
      const studentLessons = lessons.filter(lesson => lesson.studentId === student.studentId || lesson.studentId === student.id);
      // Find the most recent COMPLETED lesson
      const completedLessons = studentLessons.filter(lesson => {
        const lessonDate = new Date(lesson.startAt);
        return lesson.status === 'COMPLETED' && lessonDate <= now;
      }).sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime());
      if (completedLessons.length > 0) {
        setLastLesson(completedLessons[0]);
      } else {
        setLastLesson(null);
      }
    } catch (error) {
      console.error('Error loading lesson data:', error);
      setLastLesson(null);
    } finally {
      setLoadingLesson(false);
    }
  }, [student, lessons]);
  // Load recent tasks when student changes
  useEffect(() => {
    const loadTasks = async () => {
      if (!student || !currentUser?.uid) {
        setRecentTasks([]);
        return;
      }
      setLoadingTasks(true);
      try {
        const allTasks = await getTeacherTasks(currentUser.uid);
        // Filter tasks for this student
        const studentTasks = allTasks.filter(task => task.studentId === student.studentId || task.studentId === student.id);
        // Sort by due date (most recent first) and take last 2
        const sortedTasks = studentTasks.sort((a, b) => {
          const dateA = new Date(a.dueDate || a.deadline || 0);
          const dateB = new Date(b.dueDate || b.deadline || 0);
          return dateB.getTime() - dateA.getTime();
        });
        setRecentTasks(sortedTasks.slice(0, 2));
      } catch (error) {
        console.error('Error loading tasks:', error);
        setRecentTasks([]);
      } finally {
        setLoadingTasks(false);
      }
    };
    loadTasks();
  }, [student, currentUser]);
  // Load teacher notes when student changes
  useEffect(() => {
    if (!student) {
      setTeacherNotes('');
      setNotesInput('');
      setIsEditingNotes(false);
      return;
    }
    // Load notes from student data
    const notes = student.notes || '';
    setTeacherNotes(notes);
    setNotesInput(notes);
    setIsEditingNotes(false);
  }, [student]);
  // Handle panel animation
  useEffect(() => {
    if (open) {
      setAnimation('entering');
      setTimeout(() => setAnimation('entered'), 10);
      lastActiveElement.current = document.activeElement as HTMLElement;
    } else {
      setAnimation('exiting');
      setTimeout(() => setAnimation('exited'), 300);
    }
  }, [open]);
  // Focus management
  useEffect(() => {
    if (animation === 'entered' && panelRef.current) {
      panelRef.current.focus();
    }
    if (animation === 'exited' && lastActiveElement.current) {
      lastActiveElement.current.focus();
    }
  }, [animation]);
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);
  // Don't render if not open
  if (animation === 'exited') return null;
  if (!student) return null;
  // Get progress trend styling
  const getProgressTrendClass = (trend: string) => {
    switch (trend) {
      case 'Improving':
        return darkMode ? 'bg-green-500/20 text-green-500 border border-green-500/30' : 'bg-green-500/10 text-green-500 border border-green-500/30';
      case 'Steady':
        return darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-800';
      case 'Needs Support':
        return darkMode ? 'bg-yellow-900/30 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      default:
        return darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };
  // Get practice level color
  const getPracticeLevelColor = (level: string) => {
    switch (level) {
      case 'Excellent':
        return 'text-green-500';
      case 'Good':
        return 'text-blue-500';
      case 'Fair':
        return 'text-yellow-500';
      case 'Needs Attention':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };
  // Format date helper
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Format date with time helper
  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  // Format duration helper
  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };
  // Truncate notes helper
  const truncateNotes = (text: string, maxLength: number = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };
  // Get task status badge
  const getTaskStatusBadge = (task: Task) => {
    const now = new Date().getTime();
    const dueDateStr = task.deadline || task.dueDate;
    const dueDate = dueDateStr ? new Date(dueDateStr) : null;
    const isOverdue = dueDate && dueDate.getTime() < now && task.status !== 'completed';
    if (task.status === 'completed') {
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-10710">
          Completed
        </span>;
    }
    if (isOverdue) {
      return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'}`} data-id="element-10711">
          Overdue
        </span>;
    }
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`} data-id="element-10712">
        Pending
      </span>;
  };
  // Handle teacher notes save
  const handleSaveNotes = async () => {
    if (!currentUser?.uid || !student) return;
    setIsSavingNotes(true);
    setNotesSaveMessage('');
    try {
      await updateStudentNotes(currentUser.uid, student.id, notesInput);
      setTeacherNotes(notesInput);
      setIsEditingNotes(false);
      setNotesSaveMessage('Saved');
      // Clear success message after 2 seconds
      setTimeout(() => setNotesSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving teacher notes:', error);
      setNotesSaveMessage('Failed to save');
      setTimeout(() => setNotesSaveMessage(''), 3000);
    } finally {
      setIsSavingNotes(false);
    }
  };
  // Handle notes cancel
  const handleCancelNotes = () => {
    setNotesInput(teacherNotes);
    setIsEditingNotes(false);
    setNotesSaveMessage('');
  };
  // Handle edit button click
  const handleEditNotes = () => {
    setIsEditingNotes(true);
    setNotesSaveMessage('');
  };
  // Handle notes blur (auto-save)
  const handleNotesBlur = () => {
    if (notesInput !== teacherNotes) {
      handleSaveNotes();
    } else {
      setIsEditingNotes(false);
    }
  };
  return <>
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 ${animation === 'entered' ? 'opacity-50' : 'opacity-0'}`} onClick={onClose} aria-hidden="true" data-id="element-10713" />

      {/* Panel */}
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="progress-panel-title" tabIndex={-1} className={`fixed inset-y-0 right-0 w-full sm:w-[500px] ${darkMode ? 'bg-navy-800' : 'bg-white'} shadow-2xl z-50 overflow-y-auto transition-transform duration-300 ease-in-out ${animation === 'entered' ? 'translate-x-0' : 'translate-x-full'} md:rounded-l-2xl`} data-id="element-10714">
        {/* Header */}
        <div className={`sticky top-0 z-10 px-6 py-4 flex items-center justify-between border-b ${darkMode ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`} data-id="element-10715">
          <div className="flex items-center gap-3" data-id="element-10716">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${darkMode ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-10717">
              <UserIcon className="h-6 w-6 text-[#3B82F6]" data-id="element-10718" />
            </div>
            <div data-id="element-10719">
              <h2 id="progress-panel-title" className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-10720">
                {student.name}
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10721">
                {student.instrument || 'Unspecified'}
                {student.grade && ` · Grade ${student.grade}`}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className={`p-2 rounded-full ${darkMode ? 'hover:bg-navy-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'} transition-colors`} aria-label="Close panel" data-id="element-10722">
            <XIcon className="h-5 w-5" data-id="element-10723" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-10724">
          {/* Key Metrics */}
          <div data-id="element-10725">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-10726">
              Key Metrics
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-10727">
              {/* Attendance */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-10728">
                <div className="flex items-center gap-2 mb-2" data-id="element-10729">
                  <CalendarIcon className="h-4 w-4 text-[#3B82F6]" data-id="element-10730" />
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10731">
                    Attendance
                  </p>
                </div>
                <p className={`text-2xl font-bold ${student.attendanceValue === 0 ? darkMode ? 'text-white' : 'text-gray-900' : student.attendanceValue >= 90 ? 'text-green-500' : student.attendanceValue >= 75 ? 'text-blue-500' : 'text-red-500'}`} data-id="element-10732">
                  {student.attendanceSummary}
                </p>
              </div>

              {/* Task Completion */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-10733">
                <div className="flex items-center gap-2 mb-2" data-id="element-10734">
                  <CheckSquareIcon className="h-4 w-4 text-[#10B981]" data-id="element-10735" />
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10736">
                    Tasks Complete
                  </p>
                </div>
                <p className={`text-2xl font-bold ${parseInt(student.taskCompletionRate) === 0 ? darkMode ? 'text-white' : 'text-gray-900' : parseInt(student.taskCompletionRate) >= 80 ? 'text-green-500' : parseInt(student.taskCompletionRate) >= 60 ? 'text-blue-500' : 'text-red-500'}`} data-id="element-10737">
                  {student.taskCompletionRate}%
                </p>
              </div>
            </div>
          </div>

          {/* Teacher Notes Section */}
          <div data-id="element-10738">
            <h3 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-10739">
              Teacher Notes
            </h3>
            <div className="relative" data-id="element-10740">
              <textarea value={notesInput} onChange={e => setNotesInput(e.target.value)} rows={3} placeholder="Add notes about this student..." readOnly={!isEditingNotes} disabled={isSavingNotes} className={`w-full px-3 py-2 pr-10 rounded-lg border resize-none transition-colors ${!isEditingNotes ? 'cursor-default' : ''} ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'} ${isEditingNotes ? darkMode ? 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]' : 'focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]' : ''} ${isSavingNotes ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-10741" />

              {/* Edit button (view mode) */}
              {!isEditingNotes && <button onClick={handleEditNotes} className={`absolute top-2 right-2 p-1.5 rounded transition-colors ${darkMode ? 'text-[#10B981] hover:bg-navy-800' : 'text-[#10B981] hover:bg-gray-100'}`} aria-label="Edit notes" data-id="element-10742">
                  <PencilIcon className="h-4 w-4" data-id="element-10743" />
                </button>}

              {/* Save and Cancel buttons (edit mode) */}
              {isEditingNotes && <div className="absolute top-2 right-2 flex items-center gap-1" data-id="element-10744">
                  <button onClick={handleCancelNotes} disabled={isSavingNotes} className={`p-1.5 rounded transition-colors ${darkMode ? 'text-gray-400 hover:bg-navy-800 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'} ${isSavingNotes ? 'opacity-50 cursor-not-allowed' : ''}`} aria-label="Cancel" data-id="element-10745">
                    <XIconSmall className="h-4 w-4" data-id="element-10746" />
                  </button>
                  <button onClick={handleSaveNotes} disabled={isSavingNotes || notesInput === teacherNotes} className={`p-1.5 rounded transition-colors ${darkMode ? 'text-[#10B981] hover:bg-navy-800' : 'text-[#10B981] hover:bg-gray-100'} ${isSavingNotes || notesInput === teacherNotes ? 'opacity-50 cursor-not-allowed' : ''}`} aria-label="Save" data-id="element-10747">
                    <CheckIcon className="h-4 w-4" data-id="element-10748" />
                  </button>
                </div>}

              {notesSaveMessage && <p className={`text-xs mt-1 ${notesSaveMessage.includes('Failed') ? 'text-red-500' : 'text-[#10B981]'}`} data-id="element-10749">
                  {notesSaveMessage}
                </p>}
            </div>
          </div>

          {/* Recent Activity Section */}
          <div data-id="element-10750">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-10751">
              Recent Activity
            </h3>
            <div className="space-y-3" data-id="element-10752">
              {/* Last Lesson */}
              <div data-id="element-10753">
                <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10754">
                  Last Lesson
                </p>
                {loadingLesson ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} text-center`} data-id="element-10755">
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10756">
                      Loading...
                    </p>
                  </div> : lastLesson ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-10757">
                    <div className="flex items-start justify-between mb-2" data-id="element-10758">
                      <div className="flex-1" data-id="element-10759">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-10760">
                          {formatDate(lastLesson.startAt)}
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10761">
                          {formatDuration(lastLesson.durationMinutes)}
                        </p>
                      </div>
                      <FileTextIcon className="h-5 w-5 text-[#3B82F6]" data-id="element-10762" />
                    </div>
                    {lastLesson.notes && <p className={`text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-10763">
                        {truncateNotes(lastLesson.notes)}
                      </p>}
                  </div> : <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} text-center`} data-id="element-10764">
                    <BookOpenIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-10765" />
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10766">
                      No recent lessons
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Recent Tasks */}
          <div data-id="element-10767">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-10768">
              Recent Tasks
            </h3>
            {loadingTasks ? <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} text-center`} data-id="element-10769">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10770">
                  Loading...
                </p>
              </div> : recentTasks.length > 0 ? <div className="space-y-3" data-id="element-10771">
                {recentTasks.map(task => <div key={task.id} className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'}`} data-id="element-10772">
                    <div className="flex items-start justify-between mb-2" data-id="element-10773">
                      <div className="flex-1 min-w-0" data-id="element-10774">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'} truncate`} data-id="element-10775">
                          {task.title}
                        </p>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10776">
                          Due: {formatDate(task.dueDate || task.deadline || '')}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0" data-id="element-10777">
                        {getTaskStatusBadge(task)}
                      </div>
                    </div>
                  </div>)}
              </div> : <div className={`p-4 rounded-lg ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} text-center`} data-id="element-10778">
                <CheckSquareIcon className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} data-id="element-10779" />
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-10780">
                  No recent tasks
                </p>
              </div>}
          </div>

          {/* Quick Actions */}
          <div data-id="element-10781">
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-10782">
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-10783">
              <button onClick={() => {
              // Navigate to teacher calendar with student pre-selected via URL parameter
              navigate(`/teacher/calendar?studentId=${student.studentId}`);
              onClose();
            }} className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${darkMode ? 'bg-navy-900/50 hover:bg-navy-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-10784">
                <CalendarPlusIcon className="h-4 w-4 text-[#3B82F6]" data-id="element-10785" />
                <span className="text-sm font-medium" data-id="element-10786">Schedule</span>
              </button>

              <button onClick={() => {
              // Navigate to assigned tasks page with student pre-selected via URL parameter
              navigate(`/assigned-tasks?studentId=${student.studentId}`);
              onClose();
            }} className={`p-3 rounded-lg flex items-center gap-2 transition-colors ${darkMode ? 'bg-navy-900/50 hover:bg-navy-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} data-id="element-10787">
                <ListTodoIcon className="h-4 w-4 text-[#10B981]" data-id="element-10788" />
                <span className="text-sm font-medium" data-id="element-10789">Add Task</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer - View Full Profile */}
        <div className={`sticky bottom-0 p-6 border-t ${darkMode ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`} data-id="element-10790">
          <button onClick={() => {
          onViewFullProfile();
          onClose();
        }} className="w-full px-4 py-3 rounded-lg border-2 border-[#10B981] text-[#10B981] bg-transparent hover:bg-[#10B981]/10 transition-colors flex items-center justify-center gap-2 font-medium" data-id="element-10791">
            <ExternalLinkIcon className="h-4 w-4" data-id="element-10792" />
            View Full Profile
          </button>
        </div>
      </div>
    </>;
};